import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Warning, ShoppingCart, TrendUp, ArrowsLeftRight } from "@phosphor-icons/react";
import { dashboardStats, consumptionChart, listMovements } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    dashboardStats().then(setStats);
    consumptionChart({ days: 30 }).then(setChart);
    listMovements({ limit: 8 }).then(setRecent);
  }, []);

  return (
    <div className="fade-up space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Tableau de bord</div>
        <h1 className="text-4xl font-heading font-medium text-[color:var(--text-primary)] mt-1">Vue d'ensemble</h1>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Produits" value={stats?.total_products ?? "—"} hint="en catalogue" testid="stat-total" />
        <StatCard icon={Warning} label="Alertes" value={(stats?.low ?? 0) + (stats?.critical ?? 0) + (stats?.rupture ?? 0)} hint={`${stats?.rupture || 0} rupture · ${stats?.critical || 0} critique · ${stats?.low || 0} faible`} tone="warn" testid="stat-alerts" />
        <StatCard icon={ShoppingCart} label="À commander" value={stats?.to_reorder ?? 0} hint="propositions prêtes" testid="stat-to-order" />
        <StatCard icon={ArrowsLeftRight} label="Mouvements" value={stats?.movements_24h ?? 0} hint="dernières 24h" testid="stat-movements" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">Consommation</div>
              <div className="font-heading text-xl font-medium">30 derniers jours</div>
            </div>
            <div className="flex items-center gap-1 text-[color:var(--brand)] font-medium">
              <TrendUp size={20} weight="bold" />
              <span className="text-sm">Total : {chart.reduce((a, b) => a + b.value, 0).toFixed(0)}</span>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" hide />
                <Tooltip
                  contentStyle={{ borderRadius: 14, border: "1px solid #E2E8E4", padding: "10px 14px" }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString("fr-FR")}
                  formatter={(v) => [v, "Sorties"]}
                />
                <Line type="monotone" dataKey="value" stroke="#2A5A4A" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-6">
          <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] mb-3">Derniers mouvements</div>
          <ul className="space-y-3">
            {recent.length === 0 && <li className="text-sm text-[color:var(--text-muted)] py-6 text-center">Aucun mouvement encore.</li>}
            {recent.map((m) => (
              <li key={m.id} className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold ${m.type === "IN" ? "status-ok" : m.type === "OUT" ? "status-critical" : "bg-[color:var(--brand-muted)] text-[color:var(--brand)]"}`}>
                  {m.type === "IN" ? "+" : m.type === "OUT" ? "−" : "="}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.product_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                </div>
                <div className="text-sm font-semibold">{m.type === "ADJUST" ? `= ${m.quantity}` : m.quantity}</div>
              </li>
            ))}
          </ul>
          <Link to="/mouvements" className="mt-4 block text-center text-[color:var(--brand)] font-medium hover:underline" data-testid="dashboard-goto-movements">
            Voir tous les mouvements →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickLink to="/mouvements" icon={ArrowsLeftRight} label="Saisie" />
        <QuickLink to="/stock" icon={Package} label="État du stock" />
        <QuickLink to="/commandes" icon={ShoppingCart} label="Commandes" />
        <QuickLink to="/inventaire" icon={Warning} label="Inventaire" />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone, testid }) {
  const accent = tone === "warn" ? "text-amber-700" : "text-[color:var(--brand)]";
  return (
    <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">{label}</div>
        <Icon size={26} weight="duotone" className={accent} />
      </div>
      <div className="text-4xl font-heading font-semibold tracking-tight text-[color:var(--text-primary)]">{value}</div>
      <div className="text-xs text-[color:var(--text-muted)] mt-1">{hint}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-[color:var(--border-soft)] p-5 flex items-center gap-3 press-scale hover:border-[color:var(--brand)] hover:bg-[color:var(--brand-muted)]">
      <Icon size={28} weight="duotone" className="text-[color:var(--brand)]" />
      <span className="font-medium">{label}</span>
    </Link>
  );
}
